<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import LaisField from 'dashboard/components-next/LaisField.vue';
import { deliveryDelayDays } from '../composables/salesDeliveryForm';

const props = defineProps({
  form: { type: Object, required: true },
  errors: { type: Object, default: () => ({}) },
  disabled: { type: Boolean, default: false },
  today: { type: Date, default: () => new Date() },
});
const emit = defineEmits(['update']);
const { t } = useI18n();
const signatureDate = computed(() => props.form.data_assinatura || '');
const delayDays = computed(() => deliveryDelayDays(props.form, props.today));
const requiresDelayReason = computed(
  () =>
    props.form.recibo_type === 'manual' &&
    delayDays.value >= 5 &&
    delayDays.value <= 60
);
const showReason = computed(
  () =>
    props.form.recibo_type === 'manual' &&
    (requiresDelayReason.value ||
      props.errors.observacao ||
      props.form.observacao)
);
const update = (field, event) => emit('update', field, event.target.value);
</script>

<template>
  <LaisCard
    :title="t('SALES.DELIVERY.DATES.TITLE')"
    prefix-icon="i-lucide-calendar-clock"
  >
    <div class="grid gap-4 md:grid-cols-2">
      <LaisField
        v-if="form.recibo_type === 'manual'"
        :label="t('SALES.DELIVERY.DATES.SIGNATURE')"
        :error="errors.data_assinatura"
        required
      >
        <input
          :value="signatureDate"
          type="date"
          class="h-10 w-full rounded-lais-sm border border-lais-border bg-white px-3 text-sm"
          :disabled="disabled"
          @input="update('data_assinatura', $event)"
        />
      </LaisField>
      <LaisField :label="t('SALES.DELIVERY.DATES.TYPE')" required>
        <select
          :value="form.prazo_type"
          class="h-10 w-full rounded-lais-sm border border-lais-border bg-white px-3 text-sm"
          :disabled="disabled"
          @change="update('prazo_type', $event)"
        >
          <option value="1">{{ t('SALES.DELIVERY.DATES.BUSINESS') }}</option>
          <option value="2">{{ t('SALES.DELIVERY.DATES.CALENDAR') }}</option>
        </select>
      </LaisField>
      <LaisField
        :label="t('SALES.DELIVERY.DATES.DEADLINE')"
        :error="errors.prazo_escritura"
        required
      >
        <input
          :value="form.prazo_escritura"
          type="number"
          min="1"
          class="h-10 w-full rounded-lais-sm border border-lais-border bg-white px-3 text-sm"
          :disabled="disabled"
          @input="update('prazo_escritura', $event)"
        />
      </LaisField>
      <LaisField :label="t('SALES.DELIVERY.DATES.PREDICTION')">
        <input
          :value="form.data_previsao_escritura"
          type="date"
          readonly
          class="h-10 w-full rounded-lais-sm border border-lais-border bg-lais-bg-soft px-3 text-sm"
        />
      </LaisField>
    </div>
    <p
      v-if="requiresDelayReason"
      data-testid="sales-delivery-delay-notice"
      class="mt-4 rounded-lais-sm border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800"
    >
      {{ t('SALES.DELIVERY.DATES.DELAY_NOTICE', { days: delayDays }) }}
    </p>
    <LaisField
      v-if="showReason"
      class="mt-4"
      :label="t('SALES.DELIVERY.DATES.REASON')"
      :error="errors.observacao"
      required
    >
      <textarea
        :value="form.observacao"
        rows="3"
        class="w-full rounded-lais-sm border border-lais-border bg-white p-3 text-sm"
        :disabled="disabled"
        @input="update('observacao', $event)"
      />
    </LaisField>
  </LaisCard>
</template>
